import React, { useCallback, useState } from "react";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { AvatarUploadProvider } from "contexts/AvatarUploadProvider";
import { useAvatarUpload } from "contexts/avatarUpload.context";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useUser } from "@hooks/useUser";
import { useUpdateAvatarMutation } from "@api/user.api";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { HStack } from "@design-system/HStack/HStack";
import { UpdateProfileModal } from "./UpdateProfile/UpdateProfileModal";
import { EditableProfileFields } from "./UpdateProfile/types";
import styles from "./UserInfo.module.scss";
import { UpdateNameModal } from "./UpdateProfile/UpdateNameModal";
import { SettingRow } from "./SettingRow";
import { AvatarUpload } from "./AvatarUpload";

/**
 * Name and email are refused for a View-only role (PRO-11347). This is a UI
 * block only: `PATCH /user` and `PATCH /user/profile` are deliberate
 * `WORKSPACE_FREE_MUTATIONS` entries, because a profile is not workspace data
 * and phone still has to work for the receipt-phone onboarding task. So the
 * rows below refuse the click; the API stays open.
 */
const FullNameRow: React.FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const user = useUser();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    return (
        <SettingRow
            label="Full name"
            value={user.preferredName}
            buttonText="Edit full name"
            onButtonClick={() => {
                if (refuseIfViewOnly()) {
                    return;
                }
                onEdit();
            }}
        />
    );
};

const EmailRow: React.FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const user = useUser();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    return (
        <SettingRow
            label="Email address"
            value={user.email}
            buttonText="Edit email"
            onButtonClick={() => {
                if (refuseIfViewOnly()) {
                    return;
                }
                onEdit();
            }}
        />
    );
};

const PhoneNumberRow: React.FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const user = useUser();
    return (
        <SettingRow
            label="Phone number"
            value={user.phoneNumber}
            buttonText="Edit phone number"
            onButtonClick={onEdit}
        />
    );
};

const AvatarRow = () => {
    const user = useUser();
    const { open, rootProps, isUploading } = useAvatarUpload();
    const updateAvatarMutation = useUpdateAvatarMutation();
    const hasAvatar = !!user.avatar;

    return (
        <SettingRow
            label="Profile picture"
            value={
                <div className={styles.avatarPreview}>
                    <UserAvatar user={user} size="lg" />
                </div>
            }
            isLoading={isUploading ?? updateAvatarMutation.isPending}
            renderButton={() => (
                <HStack justify="end" gap={12}>
                    {hasAvatar && (
                        <ButtonWithLoader
                            variant="primary"
                            size="md"
                            loading={updateAvatarMutation.isPending}
                            onClick={() => updateAvatarMutation.mutate(null)}
                        >
                            Remove
                        </ButtonWithLoader>
                    )}
                    <div {...rootProps}>
                        <AvatarUpload>
                            <ButtonWithLoader
                                variant="primary"
                                size="md"
                                loading={isUploading ?? false}
                                onClick={open}
                            >
                                Upload picture
                            </ButtonWithLoader>
                        </AvatarUpload>
                    </div>
                </HStack>
            )}
        />
    );
};

export const UserInfo: React.FC = () => {
    const [showNameUpdate, setShowNameUpdate] = useState(false);
    const [update, setUpdate] = useState<EditableProfileFields | null>(null);

    const handleCancelUpdate = useCallback(() => {
        setUpdate(null);
    }, []);

    return (
        <AvatarUploadProvider>
            <div>
                <div className={styles.container}>
                    <FullNameRow onEdit={() => setShowNameUpdate(true)} />
                    <EmailRow onEdit={() => setUpdate("email")} />
                    <PhoneNumberRow onEdit={() => setUpdate("phoneNumber")} />
                    <AvatarRow />
                </div>

                <UpdateNameModal
                    show={showNameUpdate}
                    onClose={() => setShowNameUpdate(false)}
                />
                <UpdateProfileModal
                    update={update}
                    onClose={handleCancelUpdate}
                />
            </div>
        </AvatarUploadProvider>
    );
};
